/**
 * ConfirmationContext — provides a Promise-based `confirm()` function.
 *
 * Usage:
 * ```tsx
 * const { confirm } = useConfirmation()
 * const ok = await confirm({
 *     title: 'Delete?',
 *     message: 'Are you sure?',
 *     type: 'danger',
 * })
 * if (!ok) return
 * ```
 *
 * The `ConfirmationProvider` must wrap your app so that the modal
 * renders above all other content.
 */

import { createContext, useContext, useState, useCallback, useRef, useMemo, type ReactNode } from 'react'
import { ConfirmationModal } from './ConfirmationModal'

export interface ConfirmOptions {
    title?: string
    message: string
    confirmText?: string
    cancelText?: string
    type?: 'danger' | 'warning' | 'default'
}

interface ConfirmationContextType {
    confirm: (options: ConfirmOptions) => Promise<boolean>
}

const ConfirmationContext = createContext<ConfirmationContextType | undefined>(undefined)

interface ConfirmationProviderProps {
    children: ReactNode
}

export function ConfirmationProvider({ children }: ConfirmationProviderProps) {
    const [pending, setPending] = useState<ConfirmOptions | null>(null)
    const resolveRef = useRef<((value: boolean) => void) | null>(null)

    const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            resolveRef.current = resolve
            setPending(options)
        })
    }, [])

    const handleConfirm = useCallback(() => {
        resolveRef.current?.(true)
        resolveRef.current = null
        setPending(null)
    }, [])

    const handleCancel = useCallback(() => {
        resolveRef.current?.(false)
        resolveRef.current = null
        setPending(null)
    }, [])

    const value = useMemo(() => ({ confirm }), [confirm])

    return (
        <ConfirmationContext.Provider value={value}>
            {children}
            {pending && (
                <ConfirmationModal
                    title={pending.title}
                    message={pending.message}
                    confirmText={pending.confirmText}
                    cancelText={pending.cancelText}
                    type={pending.type}
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                />
            )}
        </ConfirmationContext.Provider>
    )
}

export function useConfirmation(): ConfirmationContextType {
    const context = useContext(ConfirmationContext)
    if (!context) {
        throw new Error('useConfirmation must be used within a ConfirmationProvider')
    }
    return context
}
